class ProductRecommendations extends HTMLElement {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;
      this.#loadRecommendations();
      break;
    }
  });

  #cachedRecommendations = {};
  #activeFetch = null;
  #emblaInstances = new Map();
  #resizeHandlers = new Map();

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    
    if (this.dataset.recommendationsPerformed === 'true') {
      requestAnimationFrame(() => {
        this.#initEmblaCarousels();
      });
    }
  }

  disconnectedCallback() {
    for (const embla of this.#emblaInstances.values()) {
      embla?.destroy();
    }
    this.#emblaInstances.clear();
    
    // Clean up resize handlers
    for (const handler of this.#resizeHandlers.values()) {
      window.removeEventListener('resize', handler);
    }
    this.#resizeHandlers.clear();
  }

  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          requestAnimationFrame(() => {
            this.#initEmblaCarousels();
          });
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }
      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  #initEmblaCarousels() {
    if (typeof EmblaCarousel === 'undefined') {
      console.warn('EmblaCarousel not loaded yet');
      return;
    }

    const carousels = this.querySelectorAll('.nf-product-recommendations-carousel');
    
    for (const carousel of carousels) {
      if (!(carousel instanceof HTMLElement)) continue;
      
      const carouselId = carousel.dataset.carouselId || carousel.id;
      if (!carouselId || this.#emblaInstances.has(carouselId)) continue;

      const container = carousel.querySelector('.nf-product-recommendations-carousel-container');
      if (!container) continue;

      const embla = EmblaCarousel(carousel, {
        align: 'start',
        containScroll: 'trimSnaps',
        dragFree: false,
        slidesToScroll: 1,
      });

      this.#emblaInstances.set(carouselId, embla);

      // Setup navigation buttons
      const prevButton = this.querySelector(`.nf-product-recommendations__button--prev[data-carousel-id="${carouselId}"]`);
      const nextButton = this.querySelector(`.nf-product-recommendations__button--next[data-carousel-id="${carouselId}"]`);

      if (prevButton instanceof HTMLButtonElement) {
        prevButton.addEventListener('click', () => embla.scrollPrev());
      }
      if (nextButton instanceof HTMLButtonElement) {
        nextButton.addEventListener('click', () => embla.scrollNext());
      }

      // Setup pagination dots
      const dotsContainer = this.querySelector(`.nf-product-recommendations__dots[data-carousel-id="${carouselId}"]`);
      
      // Calculate slides per page based on screen size
      const getSlidesPerPage = () => {
        const width = window.innerWidth;
        if (width >= 990) return 4; // Desktop: 4 slides
        if (width >= 750) return 3; // Tablet: 3 slides
        return 1.4; // Mobile: 1.4 slides (peek effect)
      };

      const generateDots = () => {
        if (!dotsContainer) return;
        
        const slides = container.querySelectorAll('.nf-product-recommendations__slide');
        const totalSlides = slides.length;
        const slidesPerPage = getSlidesPerPage();
        const totalPages = Math.ceil(totalSlides / slidesPerPage);
        
        // Only show dots if there's more than one page
        if (totalPages <= 1) {
          dotsContainer.innerHTML = '';
          return;
        }
        
        // Generate dots based on pages
        dotsContainer.innerHTML = '';
        for (let i = 0; i < totalPages; i++) {
          const dot = document.createElement('button');
          dot.className = 'nf-product-recommendations__dot';
          dot.type = 'button';
          dot.setAttribute('aria-label', `Go to page ${i + 1}`);
          dot.setAttribute('data-page-index', i.toString());
          if (i === 0) {
            dot.classList.add('nf-product-recommendations__dot--active');
          }
          
          // Calculate which slide index to scroll to for this page
          const slideIndex = Math.floor(i * slidesPerPage);
          dot.addEventListener('click', () => {
            embla.scrollTo(slideIndex);
          });
          
          dotsContainer.appendChild(dot);
        }
      };

      generateDots();

      // Update button and dot states
      const updateButtonStates = () => {
        if (prevButton instanceof HTMLButtonElement) {
          prevButton.disabled = !embla.canScrollPrev();
        }
        if (nextButton instanceof HTMLButtonElement) {
          nextButton.disabled = !embla.canScrollNext();
        }

        // Update dots based on current page
        if (dotsContainer) {
          const slides = container.querySelectorAll('.nf-product-recommendations__slide');
          const totalSlides = slides.length;
          const slidesPerPage = getSlidesPerPage();
          const totalPages = Math.ceil(totalSlides / slidesPerPage);
          
          if (totalPages <= 1) return;
          
          const selectedSlideIndex = embla.selectedScrollSnap();
          const currentPage = Math.floor(selectedSlideIndex / slidesPerPage);
          
          const dots = dotsContainer.querySelectorAll('.nf-product-recommendations__dot');
          for (let i = 0; i < dots.length; i++) {
            const dot = dots[i];
            if (dot instanceof HTMLButtonElement) {
              if (i === currentPage) {
                dot.classList.add('nf-product-recommendations__dot--active');
              } else {
                dot.classList.remove('nf-product-recommendations__dot--active');
              }
            }
          }
        }
      };

      // Regenerate dots on resize
      let resizeTimeout = null;
      const handleResize = () => {
        if (resizeTimeout !== null) {
          clearTimeout(resizeTimeout);
        }
        resizeTimeout = setTimeout(() => {
          generateDots();
          updateButtonStates();
          resizeTimeout = null;
        }, 150);
      };
      
      window.addEventListener('resize', handleResize);
      this.#resizeHandlers.set(carouselId, handleResize);

      embla.on('select', updateButtonStates);
      embla.on('reInit', updateButtonStates);
      updateButtonStates();
    }
  }

  #handleError(error) {
    console.error('Product recommendations error:', error);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
